import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { getSupabaseClient } from '../../lib/supabase';
import { 
  Wrench, 
  Package, 
  Plus, 
  CheckCircle2, 
  AlertTriangle, 
  RotateCcw, 
  RefreshCw, 
  X, 
  Search,
  Check,
  Info,
  ShieldCheck,
  Tag
} from 'lucide-react';
import { toast } from 'sonner';

export interface JobKitEquipment {
  id: string; // job_equipment id
  equipment_id: string;
  name: string;
  code?: string | null;
  source: string; // 'professional' | 'business' | 'company'
  status: string; // 'assigned' | 'returned'
  assigned_at?: string;
  returned_at?: string | null;
  return_condition?: string | null;
}

export interface JobKitMaterial {
  id: string; // job_material id
  product_id: string;
  item_name: string;
  unit_name?: string | null;
  qty_issued: number;
  qty_used?: number | null;
  qty_returned?: number | null;
  status: string; // 'issued' | 'closed'
}

interface JobKitManagerProps {
  bookingId: string;
  professionalId?: string | null;
  mode?: 'merchant' | 'professional';
  onUpdated?: () => void;
}

export function JobKitManager({
  bookingId,
  professionalId,
  mode = 'merchant',
  onUpdated
}: JobKitManagerProps) {
  const { currentTenantId, activeBranchId } = useAuth();
  const supabase = getSupabaseClient();

  const [loading, setLoading] = useState(true);
  const [equipmentList, setEquipmentList] = useState<JobKitEquipment[]>([]);
  const [materialList, setMaterialList] = useState<JobKitMaterial[]>([]);

  // Assign Equipment Modal
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [availableEquipment, setAvailableEquipment] = useState<any[]>([]);
  const [selectedEqId, setSelectedEqId] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [submittingAssign, setSubmittingAssign] = useState(false);

  // Return Equipment Modal
  const [returningItem, setReturningItem] = useState<JobKitEquipment | null>(null);
  const [returnCondition, setReturnCondition] = useState<'good' | 'fair' | 'damaged'>('good');
  const [returnNotes, setReturnNotes] = useState('');
  const [submittingReturn, setSubmittingReturn] = useState(false);

  // Issue Material Modal
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [searchedProducts, setSearchedProducts] = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [issueQty, setIssueQty] = useState(''); // blank default, decimal string
  const [submittingIssue, setSubmittingIssue] = useState(false);

  // Close Material Modal
  const [closingMaterial, setClosingMaterial] = useState<JobKitMaterial | null>(null);
  const [usedQty, setUsedQty] = useState(''); // decimal string
  const [submittingClose, setSubmittingClose] = useState(false);

  // Load Job Kit from RPC
  const fetchJobKit = useCallback(async () => {
    if (!supabase || !bookingId) return;
    setLoading(true);

    try {
      const { data, error } = await supabase.rpc('get_job_kit', {
        p_booking_id: bookingId
      });

      if (error) {
        console.warn('get_job_kit error:', error);
      } else if (data) {
        setEquipmentList(Array.isArray(data.equipment) ? data.equipment : []);
        setMaterialList(Array.isArray(data.materials) ? data.materials : []);
      }
    } catch (err: any) {
      console.error('fetchJobKit error:', err);
    } finally {
      setLoading(false);
    }
  }, [supabase, bookingId]);

  useEffect(() => {
    fetchJobKit();
  }, [fetchJobKit]);

  // Load available equipment for professional
  const openAssignModal = async () => {
    if (!supabase || !currentTenantId) return;
    try {
      // 1. Try get_service_equipment_list RPC, fallback to direct query
      let allEq: any[] = [];
      try {
        const { data: rpcData, error: rpcErr } = await supabase.rpc('get_service_equipment_list', {
          p_tenant_id: currentTenantId,
          p_branch_id: activeBranchId || null
        });
        if (!rpcErr && Array.isArray(rpcData) && rpcData.length > 0) {
          allEq = rpcData;
        }
      } catch {
        // Fallback below
      }

      if (allEq.length === 0) {
        const { data: directEq, error } = await supabase
          .from('service_equipment')
          .select('*')
          .eq('tenant_id', currentTenantId)
          .eq('is_active', true);

        if (error) {
          toast.error('Failed to load equipment list');
          return;
        }
        allEq = directEq || [];
      }

      // Rule: professional ka apna equipment sirf usi professional ki job me;
      // business equipment pehle "Issue" ho phir job me assign.
      const assignedIds = new Set(
        equipmentList.filter(e => e.status === 'assigned').map(e => e.equipment_id)
      );

      const filtered = (allEq || []).filter((eq: any) => {
        if (assignedIds.has(eq.id)) return false;

        // If professional is assigned, match ownership / custody
        if (professionalId) {
          if (eq.ownership === 'professional' && eq.issued_to_professional_id === professionalId) {
            return true;
          }
          if (eq.ownership === 'company' && eq.is_issued && eq.issued_to_professional_id === professionalId) {
            return true;
          }
          return false;
        }

        // If no professional assigned yet, show all available business equipment
        return eq.ownership === 'company' && !eq.is_issued;
      });

      setAvailableEquipment(filtered);
      setSelectedEqId(filtered[0]?.id || '');
      setAssignNotes('');
      setShowAssignModal(true);
    } catch (e: any) {
      toast.error(e.message || 'Error preparing equipment modal');
    }
  };

  const handleAssignEquipment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !selectedEqId) {
      toast.error('Please select an equipment');
      return;
    }

    setSubmittingAssign(true);
    try {
      const { error } = await supabase.rpc('assign_job_equipment', {
        p_booking_id: bookingId,
        p_equipment_id: selectedEqId,
        p_notes: assignNotes || null
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Equipment assigned to job');
        setShowAssignModal(false);
        fetchJobKit();
        onUpdated?.();
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to assign equipment');
    } finally {
      setSubmittingAssign(false);
    }
  };

  const handleReturnEquipment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !returningItem) return;

    setSubmittingReturn(true);
    try {
      const { error } = await supabase.rpc('release_job_equipment', {
        p_job_equipment_id: returningItem.id,
        p_condition: returnCondition,
        p_notes: returnNotes || null
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Equipment returned from job');
        setReturningItem(null);
        fetchJobKit();
        onUpdated?.();
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to return equipment');
    } finally {
      setSubmittingReturn(false);
    }
  };

  // Search products for materials
  const searchProducts = async (q: string) => {
    setProductSearch(q);
    if (!supabase || !currentTenantId) return;

    if (!q.trim()) {
      setSearchedProducts([]);
      return;
    }

    try {
      // 1. Call search_service_products RPC with p_booking_id and p_search
      try {
        const { data: rpcData, error: rpcErr } = await supabase.rpc('search_service_products', {
          p_booking_id: bookingId,
          p_search: q.trim()
        });
        if (!rpcErr && Array.isArray(rpcData) && rpcData.length > 0) {
          setSearchedProducts(rpcData);
          return;
        }
      } catch {
        // Fallback below
      }

      // 2. Fallback to direct products table query
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('tenant_id', currentTenantId)
        .ilike('product_name', `%${q.trim()}%`)
        .limit(8);

      if (!error && data) {
        setSearchedProducts(data);
      }
    } catch (e) {
      console.warn('Product search error:', e);
    }
  };

  const handleIssueMaterial = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !selectedProduct) {
      toast.error('Please select a material / product');
      return;
    }

    const qty = parseFloat(issueQty);
    if (isNaN(qty) || qty <= 0) {
      toast.error('Please enter a valid quantity (e.g. 1, 0.5, 2.25)');
      return;
    }

    setSubmittingIssue(true);
    try {
      const { error } = await supabase.rpc('issue_job_materials', {
        p_booking_id: bookingId,
        p_items: [
          {
            product_id: selectedProduct.id,
            quantity: qty
          }
        ]
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success(`Issued ${qty} ${selectedProduct.unit || 'units'} of ${selectedProduct.product_name}`);
        setShowIssueModal(false);
        setSelectedProduct(null);
        setIssueQty('');
        setProductSearch('');
        fetchJobKit();
        onUpdated?.();
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to issue material');
    } finally {
      setSubmittingIssue(false);
    }
  };

  const handleCloseMaterial = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !closingMaterial) return;

    const used = parseFloat(usedQty);
    if (isNaN(used) || used < 0) {
      toast.error('Please enter a valid used quantity (e.g. 0, 1.5, 2)');
      return;
    }

    if (used > closingMaterial.qty_issued) {
      toast.error(`Used quantity cannot exceed issued quantity (${closingMaterial.qty_issued})`);
      return;
    }

    setSubmittingClose(true);
    try {
      const { error } = await supabase.rpc('close_job_material', {
        p_job_material_id: closingMaterial.id,
        p_qty_used: used
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Material consumption closed. Used qty will reflect in billing.');
        setClosingMaterial(null);
        setUsedQty('');
        fetchJobKit();
        onUpdated?.();
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to close material');
    } finally {
      setSubmittingClose(false);
    }
  };

  // Helper summary text for professional banner
  const activeEq = equipmentList.filter(e => e.status === 'assigned');
  const activeMat = materialList.filter(m => m.status === 'issued');

  return (
    <div className="space-y-4">
      {/* Professional Job Kit Banner */}
      {mode === 'professional' && (activeEq.length > 0 || activeMat.length > 0) && (
        <div className="p-3.5 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-200 space-y-1 animate-in fade-in">
          <div className="flex items-center gap-2 font-bold text-xs text-amber-400">
            <Wrench size={15} />
            <span>Is job ke liye aapko diya gaya:</span>
          </div>
          <p className="text-xs text-amber-200/90 leading-relaxed font-medium">
            {activeEq.length > 0 && (
              <span className="font-semibold text-white">
                Equipment ({activeEq.length}): {activeEq.map(e => e.name).join(', ')}
              </span>
            )}
            {activeEq.length > 0 && activeMat.length > 0 && ' | '}
            {activeMat.length > 0 && (
              <span className="font-semibold text-white">
                Maal/Materials ({activeMat.length}): {activeMat.map(m => `${m.item_name} (${m.qty_issued} ${m.unit_name || ''})`).join(', ')}
              </span>
            )}
          </p>
        </div>
      )}

      {/* Main Section Header */}
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-2">
        <div className="flex items-center gap-2">
          <span className="p-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400">
            <Wrench size={15} />
          </span>
          <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">
            Job Kit: Equipment & Raw Material
          </h4>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={fetchJobKit}
            disabled={loading}
            className="text-[11px] h-7 px-2 text-slate-400 hover:text-slate-200"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </Button>

          {mode === 'merchant' && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={openAssignModal}
                className="text-xs h-7 gap-1 border-indigo-200 dark:border-indigo-800/80 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 font-semibold"
              >
                <Plus size={12} />
                <span>Assign Equipment</span>
              </Button>

              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowIssueModal(true);
                  setSelectedProduct(null);
                  setIssueQty('');
                  setProductSearch('');
                }}
                className="text-xs h-7 gap-1 border-purple-200 dark:border-purple-800/80 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/40 font-semibold"
              >
                <Plus size={12} />
                <span>Issue Material</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* 1. Equipment Sub-section */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-slate-500 font-semibold">
          <span>Assigned Equipment ({equipmentList.length})</span>
        </div>

        {equipmentList.length === 0 ? (
          <div className="p-3 text-center text-[11px] text-slate-400 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-dashed border-slate-200 dark:border-slate-800">
            No equipment assigned to this job.
          </div>
        ) : (
          <div className="space-y-2">
            {equipmentList.map(eq => {
              const isBusiness = eq.source === 'business' || eq.source === 'company';
              const isAssigned = eq.status === 'assigned';

              return (
                <div
                  key={eq.id}
                  className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs flex items-center justify-between text-xs"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-900 dark:text-white">
                        {eq.name}
                      </span>
                      {eq.code && (
                        <span className="font-mono text-[10px] text-slate-400">
                          ({eq.code})
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${
                        isBusiness 
                          ? 'bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800' 
                          : 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
                      }`}>
                        {isBusiness ? 'Business ka' : 'Professional ka apna'}
                      </span>
                    </div>

                    <div className="text-[11px] text-slate-500 flex items-center gap-2">
                      <span className={`font-semibold capitalize ${isAssigned ? 'text-amber-600' : 'text-slate-400'}`}>
                        {isAssigned ? '• In Job Custody' : `• Returned (${eq.return_condition || 'Good'})`}
                      </span>
                      {eq.returned_at && (
                        <span>at {new Date(eq.returned_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>

                  {isAssigned && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setReturningItem(eq);
                        setReturnCondition('good');
                        setReturnNotes('');
                      }}
                      className="text-xs h-7 gap-1 border-amber-300 dark:border-amber-800 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/40 font-semibold"
                    >
                      <RotateCcw size={12} />
                      <span>Return</span>
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 2. Raw Material Sub-section */}
      <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-800">
        <div className="flex items-center justify-between text-xs text-slate-500 font-semibold">
          <span>Raw Materials & Spare Parts ({materialList.length})</span>
        </div>

        {materialList.length === 0 ? (
          <div className="p-3 text-center text-[11px] text-slate-400 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-dashed border-slate-200 dark:border-slate-800">
            No materials issued for this job yet.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-800 bg-slate-50/75 dark:bg-slate-800/50 text-[10px] uppercase font-bold text-slate-400">
                  <th className="p-2.5">Item Name</th>
                  <th className="p-2.5 text-center">Issued</th>
                  <th className="p-2.5 text-center">Used</th>
                  <th className="p-2.5 text-center">Returned</th>
                  <th className="p-2.5 text-right">Status / Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {materialList.map(mat => {
                  const isIssued = mat.status === 'issued';
                  return (
                    <tr key={mat.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                      <td className="p-2.5">
                        <span className="font-semibold text-slate-900 dark:text-white block">
                          {mat.item_name}
                        </span>
                        {mat.unit_name && (
                          <span className="text-[10px] text-slate-400">{mat.unit_name}</span>
                        )}
                      </td>
                      <td className="p-2.5 text-center font-mono font-bold text-slate-800 dark:text-slate-200">
                        {mat.qty_issued}
                      </td>
                      <td className="p-2.5 text-center font-mono font-bold text-emerald-600 dark:text-emerald-400">
                        {mat.qty_used ?? '-'}
                      </td>
                      <td className="p-2.5 text-center font-mono font-bold text-slate-500">
                        {mat.qty_returned ?? '-'}
                      </td>
                      <td className="p-2.5 text-right">
                        {isIssued ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setClosingMaterial(mat);
                              setUsedQty(String(mat.qty_issued));
                            }}
                            className="text-xs h-7 px-2.5 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800 hover:bg-purple-50 dark:hover:bg-purple-950/40 font-semibold"
                          >
                            <span>Close (Used Qty)</span>
                          </Button>
                        ) : (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                            Closed
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ======================================================== */}
      {/* MODAL 1: Assign Equipment                                */}
      {/* ======================================================== */}
      {showAssignModal && (
        <div className="fixed inset-0 z-60 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
              <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400">
                <Wrench size={18} />
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  Assign Equipment to Job
                </h3>
              </div>
              <button 
                onClick={() => setShowAssignModal(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleAssignEquipment} className="space-y-3.5">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Select Equipment *
                </label>
                {availableEquipment.length === 0 ? (
                  <div className="p-3 text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/40 rounded-xl border border-amber-200 dark:border-amber-800">
                    No eligible equipment found. Note: Professional ka apna equipment ya pehle se "Issued" business tools hi job me assign ho sakte hain.
                  </div>
                ) : (
                  <select
                    value={selectedEqId}
                    onChange={e => setSelectedEqId(e.target.value)}
                    required
                    className="w-full text-xs p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white"
                  >
                    {availableEquipment.map(eq => (
                      <option key={eq.id} value={eq.id}>
                        {eq.name} ({eq.code || 'No code'}) — {eq.ownership === 'professional' ? 'Professional Owned' : 'Business Issued'}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Assignment Notes / Handover Details
                </label>
                <Input
                  value={assignNotes}
                  onChange={e => setAssignNotes(e.target.value)}
                  placeholder="e.g. Toolkit #2 given with 3 spare drill bits"
                  className="text-xs"
                />
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAssignModal(false)}
                  className="text-xs h-9"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submittingAssign || availableEquipment.length === 0}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-9 font-semibold gap-1.5"
                >
                  {submittingAssign ? <RefreshCw size={13} className="animate-spin" /> : <Check size={13} />}
                  <span>Assign to Job</span>
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL 2: Return Equipment                                */}
      {/* ======================================================== */}
      {returningItem && (
        <div className="fixed inset-0 z-60 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
              <div className="flex items-center gap-2 text-amber-600">
                <RotateCcw size={18} />
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  Return Equipment from Job
                </h3>
              </div>
              <button 
                onClick={() => setReturningItem(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleReturnEquipment} className="space-y-3.5">
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Returning tool: <strong>{returningItem.name}</strong>
              </p>

              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Return Condition *
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(['good', 'fair', 'damaged'] as const).map(cond => (
                    <button
                      key={cond}
                      type="button"
                      onClick={() => setReturnCondition(cond)}
                      className={`p-2.5 rounded-xl text-xs font-semibold capitalize border transition-all ${
                        returnCondition === cond
                          ? cond === 'damaged' 
                            ? 'bg-rose-50 text-rose-700 border-rose-500 font-bold dark:bg-rose-950/60 dark:text-rose-300'
                            : 'bg-primary/10 text-primary border-primary font-bold'
                          : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400'
                      }`}
                    >
                      {cond}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Condition Notes
                </label>
                <Input
                  value={returnNotes}
                  onChange={e => setReturnNotes(e.target.value)}
                  placeholder="Check for damages or missing accessories..."
                  className="text-xs"
                />
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setReturningItem(null)}
                  className="text-xs h-9"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submittingReturn}
                  className="bg-amber-600 hover:bg-amber-700 text-white text-xs h-9 font-semibold gap-1.5"
                >
                  {submittingReturn ? <RefreshCw size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                  <span>Confirm Return</span>
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL 3: Issue Material                                  */}
      {/* ======================================================== */}
      {showIssueModal && (
        <div className="fixed inset-0 z-60 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
              <div className="flex items-center gap-2 text-purple-600">
                <Package size={18} />
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  Issue Raw Material / Spare Parts
                </h3>
              </div>
              <button 
                onClick={() => setShowIssueModal(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleIssueMaterial} className="space-y-3.5">
              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Search Material / Product *
                </label>
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={productSearch}
                    onChange={e => searchProducts(e.target.value)}
                    placeholder="Search material name, wire, valve..."
                    className="pl-8 text-xs"
                  />
                </div>

                {searchedProducts.length > 0 && !selectedProduct && (
                  <div className="mt-1.5 max-h-40 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-800 shadow-md">
                    {searchedProducts.map(p => (
                      <button
                        type="button"
                        key={p.id}
                        onClick={() => {
                          setSelectedProduct(p);
                          setSearchedProducts([]);
                          setProductSearch(p.product_name);
                        }}
                        className="w-full p-2.5 text-left text-xs hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center justify-between"
                      >
                        <span className="font-semibold text-slate-800 dark:text-slate-200">{p.product_name}</span>
                        <span className="text-[10px] text-slate-400 font-mono">₹{p.sale_price || p.purchase_price || 0}</span>
                      </button>
                    ))}
                  </div>
                )}

                {selectedProduct && (
                  <div className="mt-2 p-2.5 rounded-xl bg-purple-50 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-800 text-xs flex items-center justify-between">
                    <div>
                      <span className="font-bold text-purple-900 dark:text-purple-200">{selectedProduct.product_name}</span>
                      <span className="text-[10px] text-purple-600 dark:text-purple-400 ml-1">
                        ({selectedProduct.unit || 'Units'})
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedProduct(null);
                        setProductSearch('');
                      }}
                      className="text-purple-400 hover:text-purple-600"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Issue Quantity (Decimal allowed, e.g. 1.5, 0.25) *
                </label>
                <Input
                  type="text"
                  inputMode="decimal"
                  required
                  value={issueQty}
                  onChange={e => {
                    const val = e.target.value;
                    if (val === '' || /^\d*\.?\d*$/.test(val)) {
                      setIssueQty(val);
                    }
                  }}
                  placeholder="e.g. 1 or 1.5 or 0.25"
                  className="text-xs font-mono"
                />
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowIssueModal(false)}
                  className="text-xs h-9"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submittingIssue || !selectedProduct}
                  className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-9 font-semibold gap-1.5"
                >
                  {submittingIssue ? <RefreshCw size={13} className="animate-spin" /> : <Package size={13} />}
                  <span>Issue Material</span>
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL 4: Close Material                                  */}
      {/* ======================================================== */}
      {closingMaterial && (
        <div className="fixed inset-0 z-60 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
              <div className="flex items-center gap-2 text-purple-600">
                <CheckCircle2 size={18} />
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  Close Material Consumption
                </h3>
              </div>
              <button 
                onClick={() => setClosingMaterial(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCloseMaterial} className="space-y-3.5">
              <div className="p-3 rounded-xl bg-purple-50 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-800 text-xs space-y-1">
                <span className="font-bold text-purple-900 dark:text-purple-200 block">
                  {closingMaterial.item_name}
                </span>
                <div className="text-[11px] text-purple-700 dark:text-purple-300 flex justify-between font-mono">
                  <span>Issued: {closingMaterial.qty_issued} {closingMaterial.unit_name || ''}</span>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Actual Used Quantity *
                </label>
                <Input
                  type="text"
                  inputMode="decimal"
                  required
                  value={usedQty}
                  onChange={e => {
                    const val = e.target.value;
                    if (val === '' || /^\d*\.?\d*$/.test(val)) {
                      setUsedQty(val);
                    }
                  }}
                  placeholder="e.g. 1 or 0.75"
                  className="text-xs font-mono"
                />
              </div>

              <div className="p-2.5 rounded-xl bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800/80 text-[11px] text-blue-700 dark:text-blue-300 flex items-start gap-2">
                <Info size={14} className="shrink-0 mt-0.5 text-blue-500" />
                <span>
                  <strong>Used qty bill me judega, bacha maal wapas:</strong> Jo qty use hui hai wo customer invoice me automatically material charge ban kar judegi, bacha hua maal returned mark ho kar stock me safely restore ho jayega.
                </span>
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setClosingMaterial(null)}
                  className="text-xs h-9"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submittingClose}
                  className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-9 font-semibold gap-1.5"
                >
                  {submittingClose ? <RefreshCw size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                  <span>Confirm Consumption</span>
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
